import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Search, Send, Paperclip, ArrowLeft, FileText, ExternalLink } from "lucide-react";
import { useI18n } from "../i18n/I18nContext";
import { useAuth } from "../auth/AuthContext";
import { Input, StatusDot, Chip } from "../components/Primitives";
import { listChats, getMessages, sendMessage, subscribeMessages, markChatRead } from "../lib/db";

export default function Messages() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const [chats, setChats] = useState([]);
  const [active, setActive] = useState(null);
  const [msgs, setMsgs] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [showChat, setShowChat] = useState(false);
  const [loadingChats, setLoadingChats] = useState(true);
  const unsubRef = useRef(null);
  const listEndRef = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!user?.id) { setLoadingChats(false); return; }
    listChats(user.id).then((rows) => {
      if (!alive) return;
      setChats(rows);
      const deepLink = params.get("chat");
      const preferred = deepLink && rows.find((c) => c.id === deepLink);
      if (preferred) { setActive(preferred); setShowChat(true); }
      else if (rows.length > 0) setActive(rows[0]);
      setLoadingChats(false);
    }).catch(() => setLoadingChats(false));
    return () => { alive = false; };
  }, [user?.id, params]);

  useEffect(() => {
    if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; }
    setMsgs([]);
    if (!active?.id) return;
    let alive = true;
    getMessages(active.id).then((rows) => {
      if (!alive) return;
      setMsgs(rows);
      // Messages are actually displayed → mark THIS chat as read for THIS user.
      if (user?.id) {
        markChatRead(active.id, user.id).finally(() => {
          window.dispatchEvent(new Event("r4pro:refresh-nav"));
        });
      }
    });
    unsubRef.current = subscribeMessages(active.id, (row) => {
      setMsgs((prev) => (prev.some((m) => m.id === row.id) ? prev : [...prev, {
        id: row.id, from: row.sender_id, text: row.content || "", textEn: row.content || "", time: "now", createdAt: row.created_at,
      }]));
      // Incoming message while the chat is open & displayed → still counts as read.
      if (user?.id && row.sender_id !== user.id) {
        markChatRead(active.id, user.id).finally(() => {
          window.dispatchEvent(new Event("r4pro:refresh-nav"));
        });
      }
    });
    return () => { alive = false; if (unsubRef.current) { unsubRef.current(); unsubRef.current = null; } };
  }, [active?.id, user?.id]);

  useEffect(() => { listEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs.length]);

  const send = async () => {
    if (!text.trim() || !active?.id || !user?.id || sending) return;
    const body = text.trim();
    const optimistic = { id: `tmp-${Date.now()}`, from: user.id, text: body, textEn: body, time: "now" };
    setMsgs((prev) => [...prev, optimistic]);
    setText("");
    setSending(true);
    try {
      await sendMessage(active.id, user.id, body);
    } catch (e) {
      console.error("send failed", e);
    } finally { setSending(false); }
  };

  const selectChat = (c) => {
    setActive(c);
    setShowChat(true);
    setParams({ chat: c.id }, { replace: true });
  };

  return (
    <div className="h-[calc(100vh-3.5rem)] lg:h-screen flex" data-testid="messages-page">
      <aside className={`${showChat ? "hidden" : "flex"} md:flex w-full md:w-80 shrink-0 border-r border-carbon-600 flex-col bg-carbon-900`}>
        <div className="p-4 border-b border-carbon-600">
          <h1 className="text-xl font-semibold tracking-tight mb-3">{t("messages.title")}</h1>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" />
            <Input placeholder={t("messages.search")} className="pl-9" data-testid="messages-search" />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loadingChats ? (
            <div className="p-4 text-ink-500 font-mono text-xs">Loading…</div>
          ) : chats.length === 0 ? (
            <div className="p-6 text-center text-xs text-ink-500" data-testid="messages-empty">
              No conversations yet.
              <div className="text-[10px] mt-1 text-ink-500">Reply to an opportunity to start a chat.</div>
            </div>
          ) : chats.map((c) => (
            <button key={c.id} onClick={() => selectChat(c)} data-testid={`conv-${c.id}`}
              className={`w-full flex items-center gap-3 p-3 hover:bg-carbon-800 transition text-left border-b border-carbon-600 ${
                active?.id === c.id ? "bg-carbon-800" : ""
              }`}
            >
              <div className="relative w-10 h-10 rounded-full bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-xs">
                {c.initials}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between">
                  <div className="text-sm font-medium truncate">{c.with}</div>
                  <div className="text-[10px] text-ink-500 font-mono shrink-0">{c.time}</div>
                </div>
                {c.request?.refCode ? (
                  <div className="text-[10px] font-mono text-electric-600 truncate">
                    {c.request.refCode} · {c.request.title}
                  </div>
                ) : (
                  <div className="text-xs text-ink-500 truncate">{lang === "it" ? c.lastMessage : c.lastMessageEn}</div>
                )}
              </div>
            </button>
          ))}
        </div>
      </aside>

      <section className={`${showChat ? "flex" : "hidden"} md:flex flex-1 flex-col bg-carbon-900`}>
        {active ? (
          <>
            <header className="border-b border-carbon-600" data-testid="chat-header">
              <div className="p-4 flex items-center gap-3">
                <button onClick={() => setShowChat(false)} data-testid="chat-back" className="md:hidden text-ink-600">
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div className="relative w-9 h-9 rounded-full bg-carbon-750 border border-carbon-600 flex items-center justify-center font-mono text-xs">
                  {active.initials}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{active.with}</div>
                  <div className="text-[11px] text-ink-500 flex items-center gap-1">
                    <StatusDot status="available" /> {t("messages.online")}
                  </div>
                </div>
              </div>

              {active.request?.refCode && (
                <div className="mx-4 mb-3 p-3 rounded-lg border border-electric-500/30 bg-electric-500/[0.05] flex items-center gap-3" data-testid="chat-request-context">
                  <FileText className="w-4 h-4 text-electric-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] font-mono uppercase tracking-widest text-electric-600">
                      {active.request.refCode} · <Chip className="!py-0 !px-1.5 !text-[10px] !inline">{active.request.status?.toUpperCase() || "OPEN"}</Chip>
                    </div>
                    <div className="text-xs text-ink-800 truncate mt-0.5">{active.request.title}</div>
                  </div>
                  <button onClick={() => navigate(`/app/requests/${active.request.refCode}`)} data-testid="chat-open-request"
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-md border border-electric-500/40 hover:bg-electric-500/10 text-electric-600 text-xs font-medium shrink-0 transition"
                  >
                    <ExternalLink className="w-3 h-3" /> {t("messages.openRequest")}
                  </button>
                </div>
              )}
            </header>

            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {msgs.length === 0 ? (
                <div className="text-center text-xs text-ink-500 mt-8">No messages yet — say hi.</div>
              ) : msgs.map((m) => {
                const mine = m.from === user?.id;
                return (
                  <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 text-sm ${
                      mine
                        ? "bg-electric-500 text-ink-900 rounded-br-md"
                        : "bg-carbon-800 border border-carbon-600 text-ink-800 rounded-bl-md"
                    }`}>
                      <div className="whitespace-pre-wrap">{lang === "it" ? m.text : (m.textEn || m.text)}</div>
                      <div className={`text-[10px] mt-1 font-mono ${mine ? "text-ink-900/70" : "text-ink-500"}`}>{m.time}</div>
                    </div>
                  </div>
                );
              })}
              <div ref={listEndRef} />
            </div>

            <footer className="p-3 border-t border-carbon-600 flex items-center gap-2">
              <button className="p-2 text-ink-500 hover:text-ink-900" data-testid="chat-attach" aria-label="Attach">
                <Paperclip className="w-4 h-4" />
              </button>
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                placeholder={t("messages.placeholder")}
                data-testid="chat-input"
                className="flex-1 bg-carbon-800 border border-carbon-600 rounded-full px-4 py-2.5 text-sm text-ink-900 placeholder:text-ink-500 focus:outline-none focus:border-electric-500"
              />
              <button onClick={send} data-testid="chat-send" disabled={sending || !text.trim()}
                className="w-10 h-10 rounded-full bg-electric-500 hover:bg-electric-400 disabled:opacity-50 flex items-center justify-center text-ink-900 transition">
                <Send className="w-4 h-4" />
              </button>
            </footer>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center text-ink-500 text-sm">{t("messages.empty")}</div>
        )}
      </section>
    </div>
  );
}
